import { useContext, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  tabClasses,
  Tabs,
  tabsClasses,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link } from 'react-router';

import DashboardContext from './DashboardContext';

export default function Header() {
  const context = useContext(DashboardContext);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  return (
    <Stack
      component="header"
      direction="row"
      sx={{
        display: 'flex',
        mx: 'auto',
        [context.theme.breakpoints.down('md')]: {
          gap: 0,
          justifyContent: 'space-between',
          margin: 0,
          pb: 1,
          pt: 1,
          px: 1.25,
        },
        [context.theme.breakpoints.up('md')]: {
          gap: 2,
          pl: '7.25%',
          pb: 0,
          pt: 2,
          px: 0,
        },
        [context.theme.breakpoints.up('lg')]: {
          gap: 3,
          pl: '9.33%',
          pt: 2.5,
        },
        [context.theme.breakpoints.up('xl')]: {
          pl: '11.42%',
          pt: 3,
        },
      }}
    >
      <Link to="/">
        <Box
          alt={`${context.organizationName} Logo`}
          component="img"
          src={`/${context.logoName}`}
          sx={{
            height: {
              xs: 48,
              md: 60,
              lg: 80,
            },
          }}
        />
      </Link>
      <Stack
        direction="column"
        justifyContent="space-between"
        sx={{
          gap: { xs: 0, md: 0.5, lg: 1.5, xl: 1.125 },
          [context.theme.breakpoints.down('md')]: {
            alignSelf: 'center',
          },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            [context.theme.breakpoints.down('md')]: {
              maxWidth: 250,
              textAlign: 'center',
            },
            [context.theme.breakpoints.down('lg')]: {
              mx: 1.25,
            },
          }}
        >
          <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
            {context.title}
          </Link>
        </Typography>
        <Tabs
          scrollButtons={false}
          slotProps={{
            indicator: {
              sx: { display: { xs: 'none', lg: 'block' } },
            },
          }}
          sx={{
            minHeight: 'unset',
            [context.theme.breakpoints.down('md')]: {
              display: 'none',
            },
            [`& .${tabsClasses.flexContainer}`]: {
              [context.theme.breakpoints.down('lg')]: {
                flexWrap: 'wrap',
              },
            },
            [`& .${tabClasses.root}`]: {
              color: 'inherit',
              minWidth: 'unset',
              mx: 2,
              px: 0,
              [context.theme.breakpoints.down('lg')]: {
                fontSize: '0.875rem',
                minHeight: 'unset',
                mx: 1.25,
                py: 1,
              },
              [context.theme.breakpoints.up('lg')]: {
                fontSize: '1rem',
                '&:first-child': {
                  ml: 0,
                },
              },
            },
            [`& .${tabClasses.selected}`]: {
              fontWeight: 'bold',
              [context.theme.breakpoints.down('lg')]: {
                textDecoration: 'underline',
                textUnderlineOffset: '4px',
              },
            },
          }}
          value={context.selectedDashboard}
          variant="scrollable"
        >
          {context.tabs.map((d, i) => (
            <Tab
              component={Link}
              label={d.tabName}
              key={d.id}
              to={`/${d.slug}`}
              value={d.slug}
            />
          ))}
        </Tabs>
      </Stack>
      <Box
        sx={{
          alignSelf: 'center',
          [context.theme.breakpoints.up('md')]: {
            display: 'none',
          },
        }}
      >
        <IconButton onClick={() => setDrawerOpen(true)}>
          <MenuIcon />
        </IconButton>
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <List>
            {context.tabs.map((d) => (
              <ListItem disablePadding disableGutters key={d.id}>
                <ListItemButton
                  onClick={() => setDrawerOpen(false)}
                  component={Link}
                  key={d.id}
                  selected={d.slug === context.selectedDashboard}
                  sx={{
                    color: 'text.primary',
                  }}
                  to={`/${d.slug}`}
                >
                  <ListItemText primary={d.tabName} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      </Box>
    </Stack>
  );
}
